import { useState, useEffect, useReducer, useRef, useCallback } from 'react';
import { createConsumer } from '@rails/actioncable';
import { buildSubscriptionParams } from '../lib/message_channel_factory';

const SEND_TIMEOUT_MS = 10_000;

function generateClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `cid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// TGW-1769 Jest から直接テストするため export（コンポーネント経由では WS イベント再現が煩雑なため）
export function messagesReducer(state, action) {
  switch (action.type) {
    case 'OPTIMISTIC_ADD':
      return [...state, action.message];

    case 'WS_REPLACE': {
      const idx = state.findIndex(
        (m) => m.client_id && m.client_id === action.client_id,
      );
      if (idx >= 0) {
        const replaced = [...state];
        replaced[idx] = { ...action.message, status: 'sent' };
        return replaced;
      }
      if (state.find((m) => m.id === action.message.id)) {
        return state;
      }
      return [...state, { ...action.message, status: 'sent' }];
    }

    case 'WS_RECEIVED': {
      if (state.find((m) => m.id === action.message.id)) {
        return state;
      }
      return [...state, { ...action.message, status: 'sent' }];
    }

    case 'RECEIVED_BATCH': {
      const existingIds = new Set(state.map((m) => m.id));
      const newOnes = action.messages
        .filter((m) => !existingIds.has(m.id))
        .map((m) => ({ ...m, status: 'sent' }));
      return [...state, ...newOnes];
    }

    // TGW-1769 上方ページング（過去分の先頭追加）
    case 'PREPEND_BATCH': {
      const existingIds = new Set(state.map((m) => m.id));
      const older = action.messages
        .filter((m) => !existingIds.has(m.id))
        .map((m) => ({ ...m, status: 'sent' }));
      return [...older, ...state];
    }

    // TGW-1769 スレッド返信の WS 受信。チャンネル直下には出さず（also_sent_to_channel を除く）、
    // ルートの reply_count / last_reply_at をインクリメントしてバッジを更新する。
    case 'THREAD_REPLY_RECEIVED': {
      const reply = action.message;
      let next = state.map((m) => {
        if (m.id !== reply.thread_root_message_id) return m;
        // @context TGW-1769 WS再接続・二重購読で同じ返信が複数回届くと reply_count が実数とズレる
        //   ため、処理済み返信IDをルート側に保持して2回目以降は無視する（Copilot指摘）。
        const seenReplyIds = m.thread_reply_ids || [];
        if (reply.id && seenReplyIds.includes(reply.id)) return m;
        // スレッドサマリのアバター（先頭3名・重複なし）もライブ更新する
        const participants = [...(m.thread_participants || [])];
        const replier = reply.user;
        if (
          replier &&
          participants.length < 3 &&
          !participants.some((p) => p.id === replier.id)
        ) {
          participants.push({
            id: replier.id,
            avatar_thumb: replier.avatar_thumb,
          });
        }
        return {
          ...m,
          reply_count: (m.reply_count || 0) + 1,
          last_reply_at: reply.created_at,
          thread_participants: participants,
          thread_reply_ids: reply.id
            ? [...seenReplyIds, reply.id]
            : seenReplyIds,
        };
      });
      if (reply.also_sent_to_channel && !state.find((m) => m.id === reply.id)) {
        next = [...next, { ...reply, status: 'sent' }];
      }
      return next;
    }

    case 'READ_UPDATED':
      return state.map((m) =>
        m.id === action.message_id
          ? { ...m, already_readers_count: action.already_readers_count }
          : m,
      );

    case 'MARK_FAILED':
      return state.map((m) =>
        m.client_id === action.clientId ? { ...m, status: 'failed' } : m,
      );

    case 'MARK_SENDING':
      return state.map((m) =>
        m.client_id === action.clientId ? { ...m, status: 'sending' } : m,
      );

    case 'MESSAGE_UPDATED':
      return state.map((m) =>
        m.id === action.message.id ? { ...m, ...action.message } : m,
      );

    default:
      return state;
  }
}

export default function useMessageChannel({
  scope,
  targetId,
  initialMessages = [],
  initialMaxMessageId = 0,
  apiEndpoints,
  identifierKey,
  currentUserId,
  onThreadReply = null,
}) {
  const [messages, dispatch] = useReducer(messagesReducer, initialMessages);
  const [isConnected, setIsConnected] = useState(true);
  const onThreadReplyRef = useRef(null);
  onThreadReplyRef.current = onThreadReply;
  const consumerRef = useRef(null);
  const subscriptionRef = useRef(null);
  const maxMessageIdRef = useRef(initialMaxMessageId);
  const wasDisconnectedRef = useRef(false);
  const sendTimeoutsRef = useRef({});

  const fetchDiff = useCallback(async () => {
    try {
      const url = `${apiEndpoints.fetch}?${identifierKey}=${targetId}&max_message_id=${maxMessageIdRef.current}`;
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        dispatch({ type: 'RECEIVED_BATCH', messages: data.messages });
        const maxId = Math.max(
          maxMessageIdRef.current,
          ...data.messages.map((m) => m.id),
        );
        maxMessageIdRef.current = maxId;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useMessageChannel] fetchDiff failed:', e);
    }
  }, [apiEndpoints.fetch, identifierKey, targetId]);

  useEffect(() => {
    if (!targetId) return undefined;

    const params = buildSubscriptionParams(scope, targetId);
    const consumer = createConsumer();
    consumerRef.current = consumer;

    subscriptionRef.current = consumer.subscriptions.create(params, {
      connected: () => {
        setIsConnected(true);
        if (wasDisconnectedRef.current) {
          fetchDiff();
        }
        wasDisconnectedRef.current = false;
      },
      disconnected: () => {
        setIsConnected(false);
        wasDisconnectedRef.current = true;
      },
      received: (data) => {
        if (data.type === 'message_created') {
          // TGW-1769 スレッド返信はチャンネル直下と別扱い（ルートのバッジ更新のみ）。
          // 呼び出し側（スレッドパネル等）には onThreadReply で生イベントを渡す。
          if (data.message && data.message.thread_root_message_id) {
            dispatch({ type: 'THREAD_REPLY_RECEIVED', message: data.message });
            if (onThreadReplyRef.current)
              onThreadReplyRef.current(data.message, data.client_id);
            maxMessageIdRef.current = Math.max(
              maxMessageIdRef.current,
              data.message.id,
            );
            return;
          }
          if (data.client_id) {
            dispatch({
              type: 'WS_REPLACE',
              client_id: data.client_id,
              message: data.message,
            });
            if (sendTimeoutsRef.current[data.client_id]) {
              clearTimeout(sendTimeoutsRef.current[data.client_id]);
              delete sendTimeoutsRef.current[data.client_id];
            }
          } else {
            dispatch({ type: 'WS_RECEIVED', message: data.message });
          }
          maxMessageIdRef.current = Math.max(
            maxMessageIdRef.current,
            data.message.id,
          );
        } else if (data.type === 'read_status_changed') {
          dispatch({
            type: 'READ_UPDATED',
            message_id: data.message_id,
            already_readers_count: data.already_readers_count,
          });
        } else if (data.type === 'message_updated') {
          dispatch({ type: 'MESSAGE_UPDATED', message: data.message });
        }
      },
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect();
        consumerRef.current = null;
      }
      Object.values(sendTimeoutsRef.current).forEach((id) => clearTimeout(id));
      sendTimeoutsRef.current = {};
    };
  }, [scope, targetId, fetchDiff]);

  const sendMessage = useCallback(
    // attachments: R2 ダイレクトアップロード済み cache_data 文字列配列。
    // attachmentPreviews: 楽観 UI 用のローカルプレビュー情報。
    // extraParams: mention_user_ids[] / mention_channel / mention_everyone 等。
    ({ body, attachments, attachmentPreviews, parentMessageId, extraParams } = {}) => {
      const clientId = generateClientId();
      const cacheDataList = Array.isArray(attachments)
        ? attachments.filter(Boolean)
        : [];

      dispatch({
        type: 'OPTIMISTIC_ADD',
        message: {
          client_id: clientId,
          id: null,
          body: body || '',
          sender_id: currentUserId,
          status: 'sending',
          created_at: new Date().toISOString(),
          user: { id: currentUserId },
          messages_like_users: [],
          already_readers_count: 0,
          attachments: (attachmentPreviews || []).map((p, i) => ({
            id: `local_${i}`,
            file_name: p.file_name,
            file_size: p.file_size,
            is_image: p.is_image,
            url: p.preview_url,
            thumb_url: p.preview_url,
          })),
        },
      });

      sendTimeoutsRef.current[clientId] = setTimeout(() => {
        dispatch({ type: 'MARK_FAILED', clientId });
        delete sendTimeoutsRef.current[clientId];
      }, SEND_TIMEOUT_MS);

      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]',
      )?.content;
      const formData = new FormData();
      if (body) formData.append('body', body);
      cacheDataList.forEach((cacheData) => {
        formData.append('attachments[]', cacheData);
      });
      if (parentMessageId)
        formData.append('parent_message_id', parentMessageId);
      if (extraParams) {
        Object.entries(extraParams).forEach(([key, value]) => {
          if (value === undefined || value === null || value === false) return;
          if (Array.isArray(value)) {
            value.forEach((v) => formData.append(`${key}[]`, v));
          } else {
            formData.append(key, value);
          }
        });
      }
      formData.append(identifierKey, targetId);
      formData.append('client_id', clientId);

      const url = apiEndpoints.send;

      fetch(url, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        credentials: 'same-origin',
        body: formData,
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
        })
        .catch(() => {
          if (sendTimeoutsRef.current[clientId]) {
            clearTimeout(sendTimeoutsRef.current[clientId]);
            delete sendTimeoutsRef.current[clientId];
          }
          dispatch({ type: 'MARK_FAILED', clientId });
        });

      return clientId;
    },
    [apiEndpoints, identifierKey, targetId, currentUserId],
  );

  const retryMessage = useCallback(
    (clientId) => {
      const failed = messages.find(
        (m) => m.client_id === clientId && m.status === 'failed',
      );
      if (!failed) return;
      dispatch({ type: 'MARK_SENDING', clientId });
      sendMessage({ body: failed.body });
    },
    [messages, sendMessage],
  );

  const prependMessages = useCallback((older) => {
    dispatch({ type: 'PREPEND_BATCH', messages: older });
  }, []);

  const updateMessage = useCallback((message) => {
    dispatch({ type: 'MESSAGE_UPDATED', message });
  }, []);

  return {
    messages,
    sendMessage,
    retryMessage,
    isConnected,
    fetchDiff,
    prependMessages,
    updateMessage,
  };
}
